import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../../../components/AppToast";
import PartnerLookupField from "../../../../components/PartnerLookupField";
import { sendSkuToWms, updateSkuToWms } from "../../../../services/skuItem/skuItem";
import "../skuMaster.css";
import {
  getErpExtensionDefaults,
  ITEM_STATUS_OPTIONS,
  ITEM_TYPES,
  CATEGORY_TREE,
  ORGANIZATION_OPTIONS,
} from "./skuFormErpDefaults";
import TabPackagingUom from "./TabPackagingUom";
import { mapSkuRecordToFormState as mapSkuApiToFormState } from "./skuFormMapUtils";

const PLACEHOLDER_SELECT = "Select…";

const PACKS = ["STD", "CASE", "PALLET", "EACH"];
const FACILITY_GROUPS = ["DEFAULT", "COLD", "HAZMAT", "RETAIL"];
const ITEM_GROUPS = ["Single", "Multiple", "Kit", "Virtual"];
const CLASSES = ["A", "B", "C", "Premium"];
const TRANSPORT_MODES = ["TL", "LTL", "Parcel", "Air", "Ocean", "Rail"];
const EQUIP_ATTRS = ["Dry", "Reefer", "Flatbed", "Tank"];
const EQUIP_TYPES = ["53_FT", "48_FT", "26_FT", "20_FT"];
const DEPARTMENTS = ["Apparel", "Footwear", "Accessories", "Home", "Electronics"];
const STOCK_CATS = ["Finished", "Raw", "WIP", "Returns", "Samples"];
const COLLECTIONS = ["Spring", "Summer", "Fall", "Holiday", "Core"];
const THEMES = ["Urban", "Outdoor", "Classic", "Athletic"];
const SEASONS = ["SS26", "FW26", "NS", "Clearance"];
const CATCH_WEIGHT_WHEN = ["Never", "Receive", "Pick", "Ship", "Always"];
const WEIGHT_UOMS = ["EA", "KG", "LB", "G"];
const QC_TYPE_OPTIONS = ["Visual", "Sampling", "AQL", "Weight check", "Dimension check", "Other"];

const SKU_FORM_TAB_LABELS = {
  basic: "Basic Info",
  merchandising: "Merchandising",
  uom: "Packaging & UOM",
  warehouse: "Warehouse & inventory",
  images: "Images & QC",
  compliance: "Compliance",
  review: "Review",
};

const SKU_FORM_TAB_ICONS = {
  basic: "bi-grid-1x2",
  merchandising: "bi-shop",
  uom: "bi-boxes",
  warehouse: "bi-building",
  images: "bi-images",
  compliance: "bi-shield-check",
  review: "bi-clipboard-check",
};

function tabForSkuError(key) {
  const basic = new Set(["storerkey", "sku", "descr", "packkey", "barcode"]);
  const uom = new Set([
    "baseUom",
    "piecesPerInnerPack",
    "innerPacksPerOuterPack",
    "piecesPerOuterPack",
    "outerPacksPerPallet",
    "inboundSerialNo",
    "inboundImei",
    "outboundSerialNo",
    "outboundImei",
  ]);
  if (basic.has(key)) return "basic";
  if (uom.has(key)) return "uom";
  if (key === "weightuom" || key === "qcType") return "warehouse";
  if (key === "images") return "images";
  return "basic";
}

const COUNTRIES = [
  { code: "US", label: "United States", flag: "🇺🇸" },
  { code: "CA", label: "Canada", flag: "🇨🇦" },
  { code: "GB", label: "United Kingdom", flag: "🇬🇧" },
  { code: "DE", label: "Germany", flag: "🇩🇪" },
  { code: "IN", label: "India", flag: "🇮🇳" },
  { code: "AU", label: "Australia", flag: "🇦🇺" },
  { code: "JP", label: "Japan", flag: "🇯🇵" },
  { code: "MX", label: "Mexico", flag: "🇲🇽" },
];

function CatchDataSummary({ checked, values }) {
  if (!checked) return <span className="badge text-bg-secondary">Not checked</span>;
  return (
    <div>
      <span className="badge text-bg-primary">Checked</span>
      <ul className="list-unstyled mb-0 mt-1">
        {values.map(({ label, value }) => (
          <li key={label}>
            <span className="text-muted">{label}:</span> {String(value || "").trim() || "—"}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SectionCard({ title, iconClass, children, accent = undefined }) {
  const borderClass = accent === "warning" ? "border border-warning border-2" : "border-0";
  return (
    <div className={`card shadow-sm ${borderClass} rounded-3 mb-3 sku-form-section`}>
      <div className="card-header bg-white border-0 border-bottom py-2 px-3">
        <h6 className="mb-0 fw-semibold text-body-secondary d-flex align-items-center gap-2 text-uppercase small letter-spacing-wide">
          {iconClass ? (
            <i
              className={`bi ${iconClass} ${accent === "warning" ? "text-warning" : "text-primary"} fs-6`}
              aria-hidden
            />
          ) : null}
          <span>{title}</span>
        </h6>
      </div>
      <div className="card-body px-3 py-2">{children}</div>
    </div>
  );
}

function FieldLabel({ htmlFor, label, required, tooltip, highlight }) {
  return (
    <label
      htmlFor={htmlFor}
      className={`form-label fw-semibold small mb-1 ${highlight ? "text-primary sku-form-highlight-label" : "text-body-secondary"}`}
    >
      {label}
      {required ? <span className="text-danger ms-1">*</span> : null}
      {tooltip ? (
        <span
          className="ms-1 text-muted"
          title={tooltip}
          role="note"
          aria-label={tooltip}
        >
          <i className="bi bi-info-circle small" aria-hidden />
        </span>
      ) : null}
    </label>
  );
}

function FormRow({ children, className = "" }) {
  return <div className={`row g-3 ${className}`}>{children}</div>;
}

const initialFormState = () => {
  const base = {
    storerkey: "",
    sku: "",
    descr: "",
    packkey: "",
    stdcube: "",
    stdgrosswgt: "",
    stdnetwgt: "",
    tareweight: "",
    toexpiredays: "",
    todeliverbydays: "",
    tobestbydays: "",
    skufacilitygroup: "",
    skugroup: "",
    skugroup2: "",
    class_: "",
    freightClass: "",
    mateabilityCode: "",
    transportationMode: "",
    equipmentAttribute: "",
    equipmentType: "",
    equipmentLength: "",
    department: "",
    stockCategory: "",
    country: "",
    itemCharacteristics1: "",
    itemCharacteristics2: "",
    collection: "",
    theme: "",
    season: "",
    style: "",
    color: "",
    size: "",
    campaignStart: "",
    campaignEnd: "",
    outboundAvgCaseWeight: "",
    outboundTolerancePct: "",
    outboundTareWeight: "",
    catchWeightWhen: "",
    inboundAvgCaseWeight: "",
    inboundTareWeight: "",
    inboundTolerancePct: "",
    weightuom: "EA",
    addwho: "",
    shelflifeindicator: "",
    lottracked: false,
    serialtracked: false,
    hazmat: false,
    regulatoryCategory: "",
    vendorSkuReference: "",
    msdsRequired: false,
    certificateRequired: false,
  };
  for (let i = 1; i <= 12; i += 1) {
    base[`lottable${i}`] = "";
  }
  return { ...base, ...getErpExtensionDefaults() };
};

function mapSkuRecordToFormState(record) {
  return mapSkuApiToFormState(record, initialFormState);
}

export default function CreateSkuForm({ onClose, editData, readOnly = false, mode = "create" }) {
  const isEdit = mode === "edit" && !!editData;
  const [formData, setFormData] = useState(() =>
    editData ? mapSkuRecordToFormState(editData) : initialFormState()
  );
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [imageSlots, setImageSlots] = useState(() =>
    [
      { key: "primary", label: "Primary Product Image" },
      { key: "outerPack", label: "Outer Pack Image" },
      { key: "innerPack", label: "Inner Pack Image" },
      { key: "eachPiece", label: "Each Piece Image" },
      { key: "label", label: "Label Image" },
      { key: "artwork", label: "Packaging Artwork" },
    ].map((d) => ({ ...d, file: null, previewUrl: null, status: "Draft", qcMandatory: false, version: "" }))
  );
  const [dragKey, setDragKey] = useState(null);
  const formPanelRef = useRef(null);

  const patchImageSlot = useCallback((key, patch) => {
    setImageSlots((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  }, []);

  const TAB_ORDER = ["basic", "merchandising", "uom", "warehouse", "images", "compliance", "review"];
  const tabIdx = (k) => TAB_ORDER.indexOf(k);

  const subCategoriesForCat = useMemo(
    () => CATEGORY_TREE.find((c) => c.code === formData.categoryCode)?.subcategories || [],
    [formData.categoryCode]
  );

  useEffect(() => {
    if (editData) {
      setFormData(mapSkuRecordToFormState(editData));
    }
  }, [editData]);

  useEffect(() => {
    if (readOnly) return undefined;
    const h = setTimeout(() => {
      const code = String(formData.sku || "").trim();
      if (!code) {
        setErrors((e) => {
          const n = { ...e };
          delete n.sku;
          return n;
        });
        return;
      }
      if (code.toUpperCase() === "TAKEN" || code.toUpperCase() === "DUPLICATE") {
        setErrors((e) => ({ ...e, sku: "SKU code must be unique (async validation)" }));
      } else {
        setErrors((e) => {
          const n = { ...e };
          if (n.sku && String(n.sku).includes("unique")) delete n.sku;
          return n;
        });
      }
    }, 450);
    return () => clearTimeout(h);
  }, [formData.sku, readOnly]);

  useEffect(() => {
    if (readOnly) return undefined;
    const h = setTimeout(() => {
      const b = String(formData.barcode || "").trim();
      if (!b) {
        setErrors((e) => {
          const n = { ...e };
          delete n.barcode;
          return n;
        });
        return;
      }
      if (b.toUpperCase() === "TAKEN") {
        setErrors((e) => ({ ...e, barcode: "Barcode must be unique (async validation)" }));
      } else {
        setErrors((e) => {
          const n = { ...e };
          if (n.barcode && String(n.barcode).includes("unique")) delete n.barcode;
          return n;
        });
      }
    }, 450);
    return () => clearTimeout(h);
  }, [formData.barcode, readOnly]);

  const validateStep = useCallback(
    (tab) => {
      const next = {};
      if (tab === "basic") {
        if (!String(formData.storerkey || "").trim()) next.storerkey = "Owner is required";
        if (!String(formData.sku || "").trim()) next.sku = "SKU code is required";
        if (!String(formData.descr || "").trim()) next.descr = "SKU name / description is required";
        if (!String(formData.packkey || "").trim()) next.packkey = "Pack is required";
      }
      if (tab === "uom") {
        if (!String(formData.baseUom || "").trim()) next.baseUom = "Base UOM is required";
        const inner = !!(formData.innerPackUom && String(formData.innerPackUom).trim());
        const outer = !!(formData.outerPackUom && String(formData.outerPackUom).trim());
        if (inner) {
        const p = parseFloat(String(formData.piecesPerInnerPack || ""));
        if (!Number.isFinite(p) || p <= 0) next.piecesPerInnerPack = "Must be greater than 0";
        }
        if (outer) {
          const a = parseFloat(String(formData.innerPacksPerOuterPack || ""));
          const b = parseFloat(String(formData.piecesPerOuterPack || ""));
          if (!Number.isFinite(a) || a <= 0) next.innerPacksPerOuterPack = "Must be greater than 0";
          if (!Number.isFinite(b) || b <= 0) next.piecesPerOuterPack = "Must be greater than 0";
        }
        const pal = !!(formData.palletUom && String(formData.palletUom).trim());
        if (pal) {
          const opp = parseFloat(String(formData.outerPacksPerPallet || ""));
          if (!Number.isFinite(opp) || opp <= 0) next.outerPacksPerPallet = "Must be greater than 0";
        }
        if (formData.inboundCatchData) {
          if (!String(formData.inboundSerialNo || "").trim()) next.inboundSerialNo = "Serial No is required";
          if (!String(formData.inboundImei || "").trim()) next.inboundImei = "IMEI is required";
        }
        if (formData.outboundCatchData) {
          if (!String(formData.outboundSerialNo || "").trim()) next.outboundSerialNo = "Serial No is required";
          if (!String(formData.outboundImei || "").trim()) next.outboundImei = "IMEI is required";
        }
      }
      if (tab === "warehouse") {
        if (
          formData.qcRequired &&
          !String(formData.qcType || "").trim() &&
          !String(formData.inspectionTemplateId || "").trim()
        ) {
          next.qcType = "QC type or inspection template is required";
        }
      }
      if (tab === "images") {
        const missingMandatory = imageSlots.some((s) => s.qcMandatory && !s.file);
        if (missingMandatory) next.images = "Upload all QC-mandatory reference images";
      }
      return next;
    },
    [formData, imageSlots]
  );

  const scrollFormPanelToTop = useCallback(() => {
    formPanelRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, []);

  const goToTab = useCallback(
    (targetTab, { validateAllPrior = false } = {}) => {
      const fromIdx = TAB_ORDER.indexOf(activeTab);
      const toIdx = TAB_ORDER.indexOf(targetTab);
      if (toIdx < 0 || toIdx === fromIdx) return true;

      if (!readOnly && toIdx > fromIdx) {
        const startIdx = validateAllPrior ? 0 : fromIdx;
        const stepErrs = {};
        for (let i = startIdx; i < toIdx; i += 1) {
          Object.assign(stepErrs, validateStep(TAB_ORDER[i]));
        }
        if (Object.keys(stepErrs).length) {
          setErrors((e) => ({ ...e, ...stepErrs }));
          for (let i = startIdx; i < toIdx; i += 1) {
            const tabErrs = validateStep(TAB_ORDER[i]);
            if (Object.keys(tabErrs).length) {
              setActiveTab(TAB_ORDER[i]);
              const firstKey = Object.keys(tabErrs)[0];
              toast.warning(tabErrs[firstKey] || "Complete this section before continuing.");
              requestAnimationFrame(() => {
                document.getElementById(firstKey)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
              });
              break;
            }
          }
          return false;
        }
      }

      setActiveTab(targetTab);
      scrollFormPanelToTop();
      return true;
    },
    [activeTab, validateStep, scrollFormPanelToTop, readOnly]
  );

  const goNext = useCallback(() => {
    const i = TAB_ORDER.indexOf(activeTab);
    if (i < 0 || i >= TAB_ORDER.length - 1) return;
    goToTab(TAB_ORDER[i + 1]);
  }, [activeTab, goToTab]);

  const goPrev = useCallback(() => {
    const i = TAB_ORDER.indexOf(activeTab);
    if (i <= 0) return;
    setActiveTab(TAB_ORDER[i - 1]);
    scrollFormPanelToTop();
  }, [activeTab, scrollFormPanelToTop]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      if (name === "inboundCatchData" && !checked) {
        delete next.inboundSerialNo;
        delete next.inboundImei;
      }
      if (name === "outboundCatchData" && !checked) {
        delete next.outboundSerialNo;
        delete next.outboundImei;
      }
      return next;
    });
  }, []);

  const requiredFields = useMemo(
    () => [
      { name: "storerkey", label: "Owner" },
      { name: "sku", label: "SKU code" },
      { name: "descr", label: "Description" },
      { name: "packkey", label: "Pack" },
      { name: "weightuom", label: "Weight UOM" },
    ],
    []
  );

  const lottableFields = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        name: `lottable${i + 1}`,
        label: `Lottable ${i + 1}`,
      })),
    []
  );

  const validate = useCallback(
    ({ focus = false } = {}) => {
      const next = {};
      requiredFields.forEach(({ name, label }) => {
        const v = formData[name];
        if (v === undefined || v === null || String(v).trim() === "") {
          next[name] = `${label} is required`;
        }
      });
      TAB_ORDER.forEach((t) => Object.assign(next, validateStep(t)));
      if (formData.sku && String(formData.sku).toUpperCase().match(/^(TAKEN|DUPLICATE)$/)) {
        next.sku = "SKU code must be unique";
      }
      if (formData.barcode && String(formData.barcode).toUpperCase() === "TAKEN") {
        next.barcode = "Barcode must be unique";
      }
      setErrors(next);
      const firstKey = Object.keys(next)[0];
      if (!firstKey) return true;
      if (focus) {
        setActiveTab(tabForSkuError(firstKey));
        toast.error(next[firstKey] || "Please fix validation errors");
        requestAnimationFrame(() => {
          document.getElementById(firstKey)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        });
      }
      return false;
    },
    [formData, requiredFields, validateStep]
  );

  const buildPayload = useCallback(() => {
    const n = (k) => Number(formData[k] || 0);
    const flag = (v) => (v === true || v === 1 || v === "1" ? 1 : 0);
    const inboundCatchData = flag(formData.inboundCatchData);
    const outboundCatchData = flag(formData.outboundCatchData);
    const catchText = (on, key) => (on ? String(formData[key] || "").trim() : "");
    const priorWeight =
      formData.weightData && typeof formData.weightData === "object" ? formData.weightData : {};
    const weightData = {
      ...priorWeight,
      icdflag: inboundCatchData,
      icdlabel1: catchText(inboundCatchData, "inboundSerialNo"),
      icdlabel2: catchText(inboundCatchData, "inboundImei"),
      icdlabel3: catchText(inboundCatchData, "inboundOther3"),
      icdlabel4: catchText(inboundCatchData, "inboundOther4"),
      icdlabel5: catchText(inboundCatchData, "inboundOther5"),
      ocdflag: outboundCatchData,
      ocdlabel1: catchText(outboundCatchData, "outboundSerialNo"),
      ocdlabel2: catchText(outboundCatchData, "outboundImei"),
      ocdlabel3: catchText(outboundCatchData, "outboundOther3"),
      ocdlabel4: catchText(outboundCatchData, "outboundOther4"),
      ocdlabel5: catchText(outboundCatchData, "outboundOther5"),
      avgcaseweight: Number(formData.inboundAvgCaseWeight ?? priorWeight.avgcaseweight ?? 0),
      oavgcaseweight: Number(formData.outboundAvgCaseWeight ?? priorWeight.oavgcaseweight ?? 0),
      tolerancepct: Number(formData.inboundTolerancePct ?? priorWeight.tolerancepct ?? 0),
      otolerancepct: Number(formData.outboundTolerancePct ?? priorWeight.otolerancepct ?? 0),
      tareweight: Number(formData.inboundTareWeight ?? priorWeight.tareweight ?? 0),
      otareweight: Number(formData.outboundTareWeight ?? priorWeight.otareweight ?? 0),
      wgtuom: formData.weightuom || priorWeight.wgtuom || "",
      ocdcatchwhen: formData.catchWeightWhen || priorWeight.ocdcatchwhen || "",
      voicegroupingid:
        String(formData.voiceGroupingId ?? "").trim() === ""
          ? priorWeight.voicegroupingid ?? 0
          : Number(formData.voiceGroupingId),
    };
    return {
      ...formData,
      inboundCatchData,
      inboundSerialNo: weightData.icdlabel1,
      inboundImei: weightData.icdlabel2,
      inboundOther3: weightData.icdlabel3,
      inboundOther4: weightData.icdlabel4,
      inboundOther5: weightData.icdlabel5,
      outboundCatchData,
      outboundSerialNo: weightData.ocdlabel1,
      outboundImei: weightData.ocdlabel2,
      outboundOther3: weightData.ocdlabel3,
      outboundOther4: weightData.ocdlabel4,
      outboundOther5: weightData.ocdlabel5,
      weightData,
      rfdefaultuom: formData.inventoryUom || formData.weightuom || "EA",
      grosswgt: n("stdgrosswgt"),
      netwgt: n("stdnetwgt"),
      tareweight: n("tareweight"),
      cube: n("stdcube"),
      class_: formData.class_ || "",
      skugroup: formData.skugroup || "",
      skugroup2: formData.skugroup2 || "",
      descr: formData.descr || "",
      addwho: formData.addwho || "",
      shelflifeindicator: formData.shelflifeindicator || "",
      skuImageMeta: imageSlots.map((s) => ({
        key: s.key,
        status: s.status,
        qcMandatory: s.qcMandatory,
        version: s.version || "",
        fileName: s.file?.name || null,
      })),
    };
  }, [formData, imageSlots]);

  const handleSubmit = async () => {
    if (!validate({ focus: true })) {
      return;
    }

    const payload = buildPayload();

    try {
      setLoading(true);
      const wmsPayload = { ...payload, workflowStatus: "Submitted" };
      if (isEdit) {
        await updateSkuToWms(wmsPayload);
        toast.success("SKU updated successfully");
      } else {
        await sendSkuToWms(wmsPayload);
        toast.success("SKU submitted successfully");
      }
      if (onClose) onClose();
    } catch (err) {
      toast.error(err?.message || (isEdit ? "Failed to update SKU" : "Failed to create SKU"));
    } finally {
      setLoading(false);
    }
  };

  const inputHighlight = (name) =>
    ["storerkey", "sku", "descr"].includes(name) ? "sku-form-highlight-control" : "";

  const invalid = (name) => (errors[name] ? "is-invalid" : "");

  return (
    <div className="create-sku-form">
      <style>{`
        .create-sku-form .sku-form-section { box-shadow: 0 0.125rem 0.5rem rgba(33, 37, 41, 0.06) !important; }
        .create-sku-form .sku-form-highlight-control { border-color: rgba(var(--bs-primary-rgb), 0.45); box-shadow: 0 0 0 0.12rem rgba(var(--bs-primary-rgb), 0.12); }
        .create-sku-form .sku-owner-lookup .form-control,
        .create-sku-form .sku-owner-lookup .btn { padding: 0.25rem 0.5rem; font-size: 0.875rem; }
        .create-sku-form .sku-owner-lookup .form-control { border-color: rgba(var(--bs-primary-rgb), 0.45); box-shadow: 0 0 0 0.12rem rgba(var(--bs-primary-rgb), 0.12); }
        .create-sku-form .sku-owner-lookup .form-control.is-invalid { border-color: var(--bs-form-invalid-border-color); box-shadow: 0 0 0 0.12rem rgba(var(--bs-danger-rgb), 0.15); }
      `}</style>

      <div className="sku-mgmt-master-stepper sku-mgmt-master-stepper--icons" role="tablist" aria-label="Form sections">
        {TAB_ORDER.map((id, idx) => {
          const active = activeTab === id;
          const done = tabIdx(activeTab) > idx;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`step btn btn-link text-decoration-none text-center p-0 ${active ? "active" : ""} ${done ? "done" : ""}`}
              onClick={() => goToTab(id, { validateAllPrior: !readOnly })}
            >
              <span className="step-dot" aria-hidden>
                {done ? <i className="bi bi-check" /> : <i className={`bi ${SKU_FORM_TAB_ICONS[id]}`} />}
              </span>
              <span className="step-label">{SKU_FORM_TAB_LABELS[id]}</span>
            </button>
          );
        })}
      </div>

      <div className="create-sku-form-scroll">
      <fieldset
        className={`border-0 p-0 m-0 min-width-0 w-100 ${readOnly ? "sku-form-readonly" : ""}`}
      >
      <div ref={formPanelRef} className="sku-form-tab-panel create-sku-form-body rounded p-2 p-md-3">
        {activeTab === "basic" && (
          <div className="tab-pane fade show active">
            <SectionCard title="Primary identifiers" iconClass="bi-box-seam">
              <FormRow>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel
                    htmlFor="storerkey"
                    label="Owner (storerkey)"
                    required
                    tooltip="Warehouse storer / owner account this SKU belongs to."
                    highlight
                  />
                  <div className="sku-owner-lookup">
                    <PartnerLookupField
                      id="storerkey"
                      name="storerkey"
                      value={formData.storerkey}
                      disabled={readOnly || isEdit}
                      required
                      error={errors.storerkey || ""}
                      placeholder="Search Partner"
                      modalTitle="Select Owner"
                      onSelect={(partner) =>
                        handleChange({
                          target: { name: "storerkey", value: partner?.partner_id || "" },
                        })
                      }
                    />
                  </div>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel
                    htmlFor="sku"
                    label="SKU code"
                    required
                    tooltip='Unique code. Type DUPLICATE or TAKEN to demo async uniqueness validation.'
                    highlight
                  />
                  <input
                    id="sku"
                    name="sku"
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. SKU-100245-A"
                    className={`form-control form-control-sm ${invalid("sku")} ${inputHighlight("sku")}`}
                    value={formData.sku}
                    onChange={handleChange}
                    readOnly={isEdit}
                    disabled={isEdit}
                  />
                  {errors.sku ? <div className="invalid-feedback d-block">{errors.sku}</div> : null}
                </div>
                <div className="col-12 col-lg-4">
                  <FieldLabel
                    htmlFor="descr"
                    label="SKU name / description"
                    required
                    tooltip="Primary commercial title for the SKU."
                    highlight
                  />
                  <input
                    id="descr"
                    name="descr"
                    type="text"
                    placeholder="Commercial name / product title"
                    className={`form-control form-control-sm ${invalid("descr")} ${inputHighlight("descr")}`}
                    value={formData.descr}
                    onChange={handleChange}
                  />
                  {errors.descr ? <div className="invalid-feedback d-block">{errors.descr}</div> : null}
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Basic identification (ERP)" iconClass="bi-fingerprint">
              <FormRow>
                <div className="col-md-4">
                  <FieldLabel htmlFor="shortDescription" label="Short description" />
                  <input
                    id="shortDescription"
                    name="shortDescription"
                    className="form-control form-control-sm"
                    value={formData.shortDescription}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="barcode" label="Barcode / EAN / UPC" tooltip='Use code "TAKEN" to demo async duplicate error.' />
                  <input
                    id="barcode"
                    name="barcode"
                    className={`form-control form-control-sm ${invalid("barcode")}`}
                    value={formData.barcode}
                    onChange={handleChange}
                  />
                  {errors.barcode ? <div className="invalid-feedback d-block">{errors.barcode}</div> : null}
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="alternateSkuCode" label="Alternate SKU code" />
                  <input
                    id="alternateSkuCode"
                    name="alternateSkuCode"
                    className="form-control form-control-sm"
                    value={formData.alternateSkuCode}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <FieldLabel htmlFor="itemStatus" label="Item status" />
                  <select id="itemStatus" name="itemStatus" className="form-select form-select-sm" value={formData.itemStatus} onChange={handleChange}>
                    {ITEM_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <FieldLabel htmlFor="itemType" label="Item type" />
                  <select id="itemType" name="itemType" className="form-select form-select-sm" value={formData.itemType} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {ITEM_TYPES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <FieldLabel htmlFor="categoryCode" label="Category" />
                  <select
                    id="categoryCode"
                    name="categoryCode"
                    className="form-select form-select-sm"
                    value={formData.categoryCode}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((p) => ({ ...p, categoryCode: v, subCategoryCode: "" }));
                      setErrors((prev) => {
                        const n = { ...prev };
                        delete n.categoryCode;
                        delete n.subCategoryCode;
                        return n;
                      });
                    }}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {CATEGORY_TREE.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <FieldLabel htmlFor="subCategoryCode" label="Subcategory" />
                  <select
                    id="subCategoryCode"
                    name="subCategoryCode"
                    className="form-select form-select-sm"
                    value={formData.subCategoryCode}
                    onChange={handleChange}
                    disabled={!formData.categoryCode}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {subCategoriesForCat.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="subBrand" label="Sub-brand" />
                  <input id="subBrand" name="subBrand" className="form-control form-control-sm" value={formData.subBrand} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="variantType" label="Variant type" />
                  <input id="variantType" name="variantType" className="form-control form-control-sm" value={formData.variantType} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="variantCode" label="Variant code" />
                  <input id="variantCode" name="variantCode" className="form-control form-control-sm" value={formData.variantCode} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="tenantId" label="Tenant ID" tooltip="Read-only / system" />
                  <input id="tenantId" name="tenantId" className="form-control form-control-sm bg-light" readOnly value={formData.tenantId || "—"} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="organizationId" label="Organization" />
                  <select id="organizationId" name="organizationId" className="form-select form-select-sm" value={formData.organizationId} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {ORGANIZATION_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="merchantHierarchyId" label="Merchant hierarchy ID" />
                  <input
                    id="merchantHierarchyId"
                    name="merchantHierarchyId"
                    className="form-control form-control-sm"
                    value={formData.merchantHierarchyId}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Pack & physical defaults" iconClass="bi-rulers">
              <FormRow>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="packkey" label="Pack (packkey)" required tooltip="Default handling UOM / pack key." />
                  <select
                    id="packkey"
                    name="packkey"
                    className={`form-select form-select-sm ${invalid("packkey")}`}
                    value={formData.packkey}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {PACKS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  {errors.packkey ? <div className="invalid-feedback d-block">{errors.packkey}</div> : null}
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="stdcube" label="Cube (stdcube)" tooltip="Standard cubic volume for this pack." />
                  <input
                    id="stdcube"
                    name="stdcube"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="0"
                    className={`form-control form-control-sm ${invalid("stdcube")}`}
                    value={formData.stdcube}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="skufacilitygroup" label="Item facility group" tooltip="Routing group for facility logic." />
                  <select
                    id="skufacilitygroup"
                    name="skufacilitygroup"
                    className="form-select form-select-sm"
                    value={formData.skufacilitygroup}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {FACILITY_GROUPS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="stdgrosswgt" label="Gross weight (stdgrosswgt)" />
                  <input
                    id="stdgrosswgt"
                    name="stdgrosswgt"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="0"
                    className="form-control form-control-sm"
                    value={formData.stdgrosswgt}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="stdnetwgt" label="Net weight (stdnetwgt)" />
                  <input
                    id="stdnetwgt"
                    name="stdnetwgt"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="0"
                    className="form-control form-control-sm"
                    value={formData.stdnetwgt}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="tareweight" label="Tare weight" />
                  <input
                    id="tareweight"
                    name="tareweight"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="0"
                    className="form-control form-control-sm"
                    value={formData.tareweight}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Shelf-life offsets (days)" iconClass="bi-calendar-week">
              <FormRow>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="toexpiredays" label="To expire days" tooltip="Days before expiry to flag product." />
                  <input
                    id="toexpiredays"
                    name="toexpiredays"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="—"
                    className="form-control form-control-sm"
                    value={formData.toexpiredays}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="todeliverbydays" label="To deliver by days" tooltip="Target delivery window from receipt." />
                  <input
                    id="todeliverbydays"
                    name="todeliverbydays"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="—"
                    className="form-control form-control-sm"
                    value={formData.todeliverbydays}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="tobestbydays" label="To best-by days" tooltip="Days to best-by from manufacturing or receipt." />
                  <input
                    id="tobestbydays"
                    name="tobestbydays"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="—"
                    className="form-control form-control-sm"
                    value={formData.tobestbydays}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="addwho" label="Created by (addwho)" tooltip="Optional audit user id." />
                  <input
                    id="addwho"
                    name="addwho"
                    type="text"
                    placeholder="User id"
                    className="form-control form-control-sm"
                    value={formData.addwho}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="shelflifeindicator" label="Shelf life indicator" />
                  <select
                    id="shelflifeindicator"
                    name="shelflifeindicator"
                    className="form-select form-select-sm"
                    value={formData.shelflifeindicator}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    <option value="ABC">ABC</option>
                    <option value="XYZ">XYZ</option>
                  </select>
                </div>
              </FormRow>
            </SectionCard>
          </div>
        )}

        {activeTab === "merchandising" && (
          <div className="tab-pane fade show active">
            <SectionCard title="Merchandising & class" iconClass="bi-diagram-3">
              <FormRow>
                <div className="col-md-6">
                  <FieldLabel htmlFor="skugroup" label="Item group 1 (skugroup)" />
                  <select id="skugroup" name="skugroup" className="form-select form-select-sm" value={formData.skugroup} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {ITEM_GROUPS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="skugroup2" label="Item group 2 (skugroup2)" />
                  <select id="skugroup2" name="skugroup2" className="form-select form-select-sm" value={formData.skugroup2} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {ITEM_GROUPS.map((o) => (
                      <option key={`g2-${o}`} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="class_" label="Class (class_)" />
                  <select id="class_" name="class_" className="form-select form-select-sm" value={formData.class_} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {CLASSES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="freightClass" label="Freight class" tooltip="NMFC / freight classification." />
                  <input
                    id="freightClass"
                    name="freightClass"
                    type="text"
                    placeholder="e.g. 70"
                    className="form-control form-control-sm"
                    value={formData.freightClass}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Merchandising & commercial UOM" iconClass="bi-bag-check">
              <FormRow>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="styleNumber" label="Style number" />
                  <input id="styleNumber" name="styleNumber" className="form-control form-control-sm" value={formData.styleNumber} onChange={handleChange} />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="designReference" label="Design reference" />
                  <input id="designReference" name="designReference" className="form-control form-control-sm" value={formData.designReference} onChange={handleChange} />
                </div>
                <div className="col-md-3 col-lg-2">
                  <FieldLabel htmlFor="colorCode" label="Color code" />
                  <input id="colorCode" name="colorCode" className="form-control form-control-sm" value={formData.colorCode} onChange={handleChange} />
                </div>
                <div className="col-md-3 col-lg-2">
                  <FieldLabel htmlFor="sizeGroup" label="Size group" />
                  <input id="sizeGroup" name="sizeGroup" className="form-control form-control-sm" value={formData.sizeGroup} onChange={handleChange} />
                </div>
                <div className="col-md-3 col-lg-2">
                  <FieldLabel htmlFor="material" label="Material" />
                  <input id="material" name="material" className="form-control form-control-sm" value={formData.material} onChange={handleChange} />
                </div>
                <div className="col-md-3 col-lg-2">
                  <FieldLabel htmlFor="finish" label="Finish" />
                  <input id="finish" name="finish" className="form-control form-control-sm" value={formData.finish} onChange={handleChange} />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="productLine" label="Product line" />
                  <input id="productLine" name="productLine" className="form-control form-control-sm" value={formData.productLine} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="launchDate" label="Launch date" />
                  <input id="launchDate" name="launchDate" type="date" className="form-control form-control-sm" value={formData.launchDate} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="discontinueDate" label="Discontinue date" />
                  <input id="discontinueDate" name="discontinueDate" type="date" className="form-control form-control-sm" value={formData.discontinueDate} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="hsnTaxCode" label="HSN / tax code" />
                  <input id="hsnTaxCode" name="hsnTaxCode" className="form-control form-control-sm" value={formData.hsnTaxCode} onChange={handleChange} />
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="salesUom" label="Sales UOM" />
                  <select id="salesUom" name="salesUom" className="form-select form-select-sm" value={formData.salesUom} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {WEIGHT_UOMS.concat(["PCS", "CASE"]).map((u) => (
                      <option key={`su-${u}`} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="pricingUom" label="Pricing UOM" />
                  <select id="pricingUom" name="pricingUom" className="form-select form-select-sm" value={formData.pricingUom} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {WEIGHT_UOMS.concat(["PCS", "CASE"]).map((u) => (
                      <option key={`pu-${u}`} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="purchaseUom" label="Purchase UOM" />
                  <select id="purchaseUom" name="purchaseUom" className="form-select form-select-sm" value={formData.purchaseUom} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {WEIGHT_UOMS.concat(["PCS", "CASE"]).map((u) => (
                      <option key={`pru-${u}`} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="inventoryUom" label="Inventory UOM" />
                  <select id="inventoryUom" name="inventoryUom" className="form-select form-select-sm" value={formData.inventoryUom} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {WEIGHT_UOMS.concat(["PCS", "CASE"]).map((u) => (
                      <option key={`iu-${u}`} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="pieceUom" label="Piece UOM" />
                  <select id="pieceUom" name="pieceUom" className="form-select form-select-sm" value={formData.pieceUom} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {WEIGHT_UOMS.concat(["PCS", "CASE"]).map((u) => (
                      <option key={`piu-${u}`} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Transportation & equipment" iconClass="bi-truck-flatbed">
              <FormRow>
                <div className="col-md-6">
                  <FieldLabel htmlFor="mateabilityCode" label="Mateability code" tooltip="Stacking / compatibility code for loading." />
                  <input
                    id="mateabilityCode"
                    name="mateabilityCode"
                    type="text"
                    placeholder="Code"
                    className="form-control form-control-sm"
                    value={formData.mateabilityCode}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="transportationMode" label="Transportation mode" />
                  <select
                    id="transportationMode"
                    name="transportationMode"
                    className="form-select form-select-sm"
                    value={formData.transportationMode}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {TRANSPORT_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="equipmentAttribute" label="Equipment attribute" />
                  <select
                    id="equipmentAttribute"
                    name="equipmentAttribute"
                    className="form-select form-select-sm"
                    value={formData.equipmentAttribute}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {EQUIP_ATTRS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="equipmentType" label="Equipment type" />
                  <select
                    id="equipmentType"
                    name="equipmentType"
                    className="form-select form-select-sm"
                    value={formData.equipmentType}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {EQUIP_TYPES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="equipmentLength" label="Equipment length" />
                  <input
                    id="equipmentLength"
                    name="equipmentLength"
                    type="text"
                    placeholder="e.g. 53 ft"
                    className="form-control form-control-sm"
                    value={formData.equipmentLength}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Fashion & assortment" iconClass="bi-bag-heart">
              <FormRow>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="department" label="Department" />
                  <select
                    id="department"
                    name="department"
                    className="form-select form-select-sm"
                    value={formData.department}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {DEPARTMENTS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="stockCategory" label="Stock category" />
                  <select
                    id="stockCategory"
                    name="stockCategory"
                    className="form-select form-select-sm"
                    value={formData.stockCategory}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {STOCK_CATS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="country" label="Country" />
                  <select id="country" name="country" className="form-select form-select-sm" value={formData.country} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="itemCharacteristics1" label="Item characteristics 1" />
                  <input
                    id="itemCharacteristics1"
                    name="itemCharacteristics1"
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.itemCharacteristics1}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="itemCharacteristics2" label="Item characteristics 2" />
                  <input
                    id="itemCharacteristics2"
                    name="itemCharacteristics2"
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.itemCharacteristics2}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="collection" label="Collection" />
                  <select
                    id="collection"
                    name="collection"
                    className="form-select form-select-sm"
                    value={formData.collection}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {COLLECTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="theme" label="Theme" />
                  <select id="theme" name="theme" className="form-select form-select-sm" value={formData.theme} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {THEMES.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="season" label="Season" />
                  <select id="season" name="season" className="form-select form-select-sm" value={formData.season} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {SEASONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="style" label="Style" />
                  <input id="style" name="style" type="text" className="form-control form-control-sm" value={formData.style} onChange={handleChange} />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="color" label="Color" />
                  <input id="color" name="color" type="text" className="form-control form-control-sm" value={formData.color} onChange={handleChange} />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="size" label="Size" />
                  <select id="size" name="size" className="form-select form-select-sm" value={formData.size} onChange={handleChange}>
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {["XS", "S", "M", "L", "XL", "XXL", "OS"].map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="campaignStart" label="Campaign start" />
                  <input
                    id="campaignStart"
                    name="campaignStart"
                    type="date"
                    className="form-control form-control-sm"
                    value={formData.campaignStart}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel htmlFor="campaignEnd" label="Campaign end" />
                  <input
                    id="campaignEnd"
                    name="campaignEnd"
                    type="date"
                    className="form-control form-control-sm"
                    value={formData.campaignEnd}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>
          </div>
        )}

        {activeTab === "uom" && (
          <div className="tab-pane fade show active">
            <TabPackagingUom formData={formData} handleChange={handleChange} errors={errors} invalid={invalid} />
            <SectionCard title="Lottable attributes (1–12)" iconClass="bi-ui-checks-grid">
              <p className="small text-muted mb-3">
                Map WMS lottable slots to fashion or regulatory attributes (e.g. style, dye lot, PO).
              </p>
              <FormRow>
                {lottableFields.map(({ name, label }) => (
                  <div className="col-sm-6 col-md-4" key={name}>
                    <FieldLabel htmlFor={name} label={label} />
                    <input
                      id={name}
                      name={name}
                      type="text"
                      placeholder={`Value for ${label}`}
                      className="form-control form-control-sm"
                      value={formData[name]}
                      onChange={handleChange}
                    />
                  </div>
                ))}
              </FormRow>
            </SectionCard>
          </div>
        )}

        {activeTab === "warehouse" && (
          <div className="tab-pane fade show active">
            <div className="alert alert-warning border-0 shadow-sm py-2 px-3 small mb-3 d-flex align-items-center gap-2">
              <i className="bi bi-clipboard2-pulse fs-5 text-warning" aria-hidden />
              <span>
                <span className="fw-semibold">Warehouse &amp; QC</span> drive receiving, allocation, and inspection. Align with operations before go-live.
              </span>
            </div>

            <SectionCard title="Storage & fulfillment strategy" iconClass="bi-building-gear">
              <FormRow>
                <div className="col-md-4">
                  <FieldLabel
                    htmlFor="warehouseStorageType"
                    label="Warehouse storage type"
                    tooltip="E.g. ambient, chilled, bulk, rack-selective."
                  />
                  <input
                    id="warehouseStorageType"
                    name="warehouseStorageType"
                    className="form-control form-control-sm"
                    value={formData.warehouseStorageType}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="putawayStrategy" label="Putaway strategy" />
                  <input
                    id="putawayStrategy"
                    name="putawayStrategy"
                    className="form-control form-control-sm"
                    value={formData.putawayStrategy}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="pickStrategy" label="Pick strategy" />
                  <input
                    id="pickStrategy"
                    name="pickStrategy"
                    className="form-control form-control-sm"
                    value={formData.pickStrategy}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="defaultWarehouse" label="Default warehouse" />
                  <input
                    id="defaultWarehouse"
                    name="defaultWarehouse"
                    className="form-control form-control-sm"
                    value={formData.defaultWarehouse}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel
                    htmlFor="storageTemperatureRange"
                    label="Storage temperature range"
                    tooltip="e.g. 2–8 °C, 15–25 °C."
                  />
                  <input
                    id="storageTemperatureRange"
                    name="storageTemperatureRange"
                    className="form-control form-control-sm"
                    value={formData.storageTemperatureRange}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="stackLimit" label="Stack limit (layers)" />
                  <input
                    id="stackLimit"
                    name="stackLimit"
                    type="number"
                    min="0"
                    className="form-control form-control-sm"
                    value={formData.stackLimit}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Inventory control & tracking" iconClass="bi-diagram-2">
              <FormRow className="align-items-end">
                <div className="col-6 col-md-4 col-lg-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="batchControlled"
                      name="batchControlled"
                      checked={formData.batchControlled}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="batchControlled">
                      Batch controlled
                    </label>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="lottracked"
                      name="lottracked"
                      checked={formData.lottracked}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="lottracked">
                      Lot controlled
                    </label>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="serialtracked"
                      name="serialtracked"
                      checked={formData.serialtracked}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="serialtracked">
                      Serial controlled
                    </label>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="expiryControlled"
                      name="expiryControlled"
                      checked={formData.expiryControlled}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="expiryControlled">
                      Expiry controlled
                    </label>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="hazmat"
                      name="hazmat"
                      checked={formData.hazmat}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="hazmat">
                      Hazardous
                    </label>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="fragileFlag"
                      name="fragileFlag"
                      checked={formData.fragileFlag}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="fragileFlag">
                      Fragile
                    </label>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="reorderSafetyFlag"
                      name="reorderSafetyFlag"
                      checked={formData.reorderSafetyFlag}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="reorderSafetyFlag">
                      Reorder / safety flag
                    </label>
                  </div>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="shelfLifeDays" label="Shelf life (days)" tooltip="Planning horizon for freshness / rotation." />
                  <input
                    id="shelfLifeDays"
                    name="shelfLifeDays"
                    type="number"
                    min="0"
                    className="form-control form-control-sm"
                    value={formData.shelfLifeDays}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="QC & inspection" iconClass="bi-clipboard2-check" accent="warning">
              <FormRow>
                <div className="col-12">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="qcRequired"
                      name="qcRequired"
                      checked={formData.qcRequired}
                      onChange={handleChange}
                    />
                    <label className="form-check-label fw-semibold small" htmlFor="qcRequired">
                      QC required before putaway / ship
                    </label>
                  </div>
                </div>
                <div className="col-md-4">
                  <FieldLabel
                    htmlFor="qcType"
                    label="QC type"
                    tooltip="Used when QC is enabled; provide this or an inspection template."
                  />
                  <select
                    id="qcType"
                    name="qcType"
                    className={`form-select form-select-sm ${invalid("qcType")}`}
                    value={formData.qcType}
                    onChange={handleChange}
                    disabled={!formData.qcRequired}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {QC_TYPE_OPTIONS.map((q) => (
                      <option key={q} value={q}>
                        {q}
                      </option>
                    ))}
                  </select>
                  {errors.qcType ? <div className="invalid-feedback d-block">{errors.qcType}</div> : null}
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="inspectionTemplateId" label="Inspection template ID" />
                  <input
                    id="inspectionTemplateId"
                    name="inspectionTemplateId"
                    className="form-control form-control-sm"
                    value={formData.inspectionTemplateId}
                    onChange={handleChange}
                    disabled={!formData.qcRequired}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <SectionCard title="Physical attributes (handling)" iconClass="bi-rulers">
              <FormRow>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="weightPerUnit" label="Weight per unit" />
                  <input
                    id="weightPerUnit"
                    name="weightPerUnit"
                    type="number"
                    min="0"
                    step="any"
                    className="form-control form-control-sm"
                    value={formData.weightPerUnit}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="volume" label="Volume" />
                  <input
                    id="volume"
                    name="volume"
                    type="number"
                    min="0"
                    step="any"
                    className="form-control form-control-sm"
                    value={formData.volume}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="lengthDim" label="Length" />
                  <input
                    id="lengthDim"
                    name="lengthDim"
                    type="number"
                    min="0"
                    step="any"
                    className="form-control form-control-sm"
                    value={formData.lengthDim}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="widthDim" label="Width" />
                  <input
                    id="widthDim"
                    name="widthDim"
                    type="number"
                    min="0"
                    step="any"
                    className="form-control form-control-sm"
                    value={formData.widthDim}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 col-lg-2">
                  <FieldLabel htmlFor="heightDim" label="Height" />
                  <input
                    id="heightDim"
                    name="heightDim"
                    type="number"
                    min="0"
                    step="any"
                    className="form-control form-control-sm"
                    value={formData.heightDim}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>

            <div className="row g-4">
              <div className="col-lg-6">
                <SectionCard title="Outbound" iconClass="bi-box-arrow-up-right">
                  <FormRow>
                    <div className="col-12">
                      <FieldLabel htmlFor="outboundAvgCaseWeight" label="Avg case weight" tooltip="Mean weight per ship case." />
                      <input
                        id="outboundAvgCaseWeight"
                        name="outboundAvgCaseWeight"
                        type="number"
                        min="0"
                        step="any"
                        placeholder="0"
                        className="form-control form-control-sm"
                        value={formData.outboundAvgCaseWeight}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <FieldLabel htmlFor="outboundTolerancePct" label="Tolerance %" tooltip="Acceptable variance vs target weight." />
                      <input
                        id="outboundTolerancePct"
                        name="outboundTolerancePct"
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="e.g. 2.5"
                        className="form-control form-control-sm"
                        value={formData.outboundTolerancePct}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <FieldLabel htmlFor="outboundTareWeight" label="Tare weight" />
                      <input
                        id="outboundTareWeight"
                        name="outboundTareWeight"
                        type="number"
                        min="0"
                        step="any"
                        className="form-control form-control-sm"
                        value={formData.outboundTareWeight}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <FieldLabel htmlFor="catchWeightWhen" label="Catch weight when" />
                      <select
                        id="catchWeightWhen"
                        name="catchWeightWhen"
                        className="form-select form-select-sm"
                        value={formData.catchWeightWhen}
                        onChange={handleChange}
                      >
                        <option value="">{PLACEHOLDER_SELECT}</option>
                        {formData.catchWeightWhen &&
                        !CATCH_WEIGHT_WHEN.includes(formData.catchWeightWhen) ? (
                          <option value={formData.catchWeightWhen}>{formData.catchWeightWhen}</option>
                        ) : null}
                        {CATCH_WEIGHT_WHEN.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  </FormRow>
                </SectionCard>
              </div>
              <div className="col-lg-6">
                <SectionCard title="Inbound" iconClass="bi-box-arrow-in-down">
                  <FormRow>
                    <div className="col-12">
                      <FieldLabel htmlFor="inboundAvgCaseWeight" label="Avg case weight" />
                      <input
                        id="inboundAvgCaseWeight"
                        name="inboundAvgCaseWeight"
                        type="number"
                        min="0"
                        step="any"
                        placeholder="0"
                        className="form-control form-control-sm"
                        value={formData.inboundAvgCaseWeight}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <FieldLabel htmlFor="inboundTareWeight" label="Tare weight" />
                      <input
                        id="inboundTareWeight"
                        name="inboundTareWeight"
                        type="number"
                        min="0"
                        step="any"
                        className="form-control form-control-sm"
                        value={formData.inboundTareWeight}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <FieldLabel htmlFor="inboundTolerancePct" label="Tolerance %" />
                      <input
                        id="inboundTolerancePct"
                        name="inboundTolerancePct"
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="e.g. 2.5"
                        className="form-control form-control-sm"
                        value={formData.inboundTolerancePct}
                        onChange={handleChange}
                      />
                    </div>
                  </FormRow>
                </SectionCard>
              </div>
            </div>

            <SectionCard title="Common" iconClass="bi-scales">
              <FormRow>
                <div className="col-md-6 col-lg-4">
                  <FieldLabel
                    htmlFor="weightuom"
                    label="Weight UOM"
                    required
                    tooltip="Default unit of measure for weight fields; sent as rfdefaultuom to WMS."
                  />
                  <select
                    id="weightuom"
                    name="weightuom"
                    className={`form-select form-select-sm ${invalid("weightuom")}`}
                    value={formData.weightuom}
                    onChange={handleChange}
                  >
                    {WEIGHT_UOMS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                  {errors.weightuom ? <div className="invalid-feedback d-block">{errors.weightuom}</div> : null}
                </div>
              </FormRow>
            </SectionCard>
          </div>
        )}

        {activeTab === "images" && (
          <div className="tab-pane fade show active">
            <SectionCard title="Product & packaging images" iconClass="bi-images">
              <p className="small text-muted mb-3">
                Drag-and-drop or upload. File bytes are not sent to WMS yet; metadata is included as{" "}
                <code>skuImageMeta</code> on save.
              </p>
              <div className="row g-3">
                {imageSlots.map((slot) => (
                  <div className="col-md-6 col-lg-4" key={slot.key}>
                    <div className="card h-100 border sku-mgmt-image-preview-card overflow-hidden">
                      <div
                        className={`sku-mgmt-image-dropzone ${dragKey === slot.key ? "drag-active" : ""}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragKey(slot.key);
                        }}
                        onDragLeave={() => setDragKey(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragKey(null);
                          const f = e.dataTransfer?.files?.[0];
                          if (!f || !f.type.startsWith("image/")) return;
                          const reader = new FileReader();
                          reader.onload = () => patchImageSlot(slot.key, { file: f, previewUrl: reader.result });
                          reader.readAsDataURL(f);
                        }}
                        onClick={() => document.getElementById(`sku-img-${slot.key}`)?.click()}
                      >
                        {slot.previewUrl ? (
                          <img src={slot.previewUrl} alt={slot.label} className="d-block w-100" style={{ maxHeight: 140, objectFit: "cover" }} />
                        ) : (
                          <div className="py-3 small text-secondary">
                            <i className="bi bi-cloud-arrow-up d-block fs-4 mb-1" />
                            Drop or click
                          </div>
                        )}
                        <input
                          id={`sku-img-${slot.key}`}
                          type="file"
                          accept="image/*"
                          className="d-none"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const reader = new FileReader();
                            reader.onload = () => patchImageSlot(slot.key, { file: f, previewUrl: reader.result });
                            reader.readAsDataURL(f);
                          }}
                        />
                      </div>
                      <div className="card-body py-2">
                        <div className="small fw-semibold">{slot.label}</div>
                        <div className="mt-2">
                          <label className="form-label small text-muted mb-0" htmlFor={`ver-${slot.key}`}>
                            Version
                          </label>
                          <input
                            id={`ver-${slot.key}`}
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g. 1.0"
                            value={slot.version}
                            onChange={(e) => patchImageSlot(slot.key, { version: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
                          <span
                            className={`badge ${
                              slot.status === "Approved" ? "bg-success" : slot.status === "Obsolete" ? "bg-danger" : "bg-secondary"
                            }`}
                          >
                            {slot.status}
                          </span>
                          <div className="form-check form-switch form-check-reverse ms-auto mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`qc-${slot.key}`}
                              checked={slot.qcMandatory}
                              onChange={(e) => patchImageSlot(slot.key, { qcMandatory: e.target.checked })}
                            />
                            <label className="form-check-label small" htmlFor={`qc-${slot.key}`}>
                              QC ref.
                            </label>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => patchImageSlot(slot.key, { status: "Draft" })}
                          >
                            Draft
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            onClick={() => patchImageSlot(slot.key, { status: "Approved" })}
                          >
                            Approved
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => patchImageSlot(slot.key, { status: "Obsolete" })}
                          >
                            Obsolete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {activeTab === "compliance" && (
          <div className="tab-pane fade show active">
            <SectionCard title="Compliance & vendor" iconClass="bi-shield-lock">
              <FormRow>
                <div className="col-md-6">
                  <FieldLabel htmlFor="countryOfOrigin" label="Country of origin" />
                  <select
                    id="countryOfOrigin"
                    name="countryOfOrigin"
                    className="form-select form-select-sm"
                    value={formData.countryOfOrigin}
                    onChange={handleChange}
                  >
                    <option value="">{PLACEHOLDER_SELECT}</option>
                    {COUNTRIES.map((c) => (
                      <option key={`coo-${c.code}`} value={c.code}>
                        {c.flag} {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="regulatoryCategory" label="Regulatory category" />
                  <input
                    id="regulatoryCategory"
                    name="regulatoryCategory"
                    className="form-control form-control-sm"
                    value={formData.regulatoryCategory}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="vendorSkuReference" label="Vendor reference" />
                  <input
                    id="vendorSkuReference"
                    name="vendorSkuReference"
                    className="form-control form-control-sm"
                    value={formData.vendorSkuReference}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="msdsRequired"
                      name="msdsRequired"
                      checked={formData.msdsRequired}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="msdsRequired">
                      MSDS required
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="certificateRequired"
                      name="certificateRequired"
                      checked={formData.certificateRequired}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="certificateRequired">
                      Certificate required
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="qcCertificateType" label="QC certificate type" />
                  <input
                    id="qcCertificateType"
                    name="qcCertificateType"
                    className="form-control form-control-sm"
                    value={formData.qcCertificateType}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="returnableFlag"
                      name="returnableFlag"
                      checked={formData.returnableFlag}
                      onChange={handleChange}
                    />
                    <label className="form-check-label small" htmlFor="returnableFlag">
                      Returnable
                    </label>
                  </div>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="damageTolerance" label="Damage tolerance %" tooltip="Acceptable damage rate on receipt." />
                  <input
                    id="damageTolerance"
                    name="damageTolerance"
                    type="number"
                    min="0"
                    step="0.1"
                    className="form-control form-control-sm"
                    value={formData.damageTolerance}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="overReceiptTolerance" label="Over receipt tolerance %" />
                  <input
                    id="overReceiptTolerance"
                    name="overReceiptTolerance"
                    type="number"
                    min="0"
                    step="0.1"
                    className="form-control form-control-sm"
                    value={formData.overReceiptTolerance}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="underReceiptTolerance" label="Under receipt tolerance %" />
                  <input
                    id="underReceiptTolerance"
                    name="underReceiptTolerance"
                    type="number"
                    min="0"
                    step="0.1"
                    className="form-control form-control-sm"
                    value={formData.underReceiptTolerance}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="packagingVarianceTolerance" label="Packaging variance tolerance %" />
                  <input
                    id="packagingVarianceTolerance"
                    name="packagingVarianceTolerance"
                    type="number"
                    min="0"
                    step="0.1"
                    className="form-control form-control-sm"
                    value={formData.packagingVarianceTolerance}
                    onChange={handleChange}
                  />
                </div>
              </FormRow>
            </SectionCard>
          </div>
        )}

        {activeTab === "review" && (
          <div className="tab-pane fade show active">
            <SectionCard title="Summary" iconClass="bi-clipboard-data">
              <dl className="row small mb-0">
                <dt className="col-sm-4">Owner</dt>
                <dd className="col-sm-8">{formData.storerkey || "—"}</dd>
                <dt className="col-sm-4">SKU</dt>
                <dd className="col-sm-8">{formData.sku || "—"}</dd>
                <dt className="col-sm-4">Description</dt>
                <dd className="col-sm-8">{formData.descr || "—"}</dd>
                <dt className="col-sm-4">Pack</dt>
                <dd className="col-sm-8">{formData.packkey || "—"}</dd>
                <dt className="col-sm-4">Weight UOM</dt>
                <dd className="col-sm-8">{formData.weightuom || "—"}</dd>
                <dt className="col-sm-4">Inbound catch data</dt>
                <dd className="col-sm-8">
                  <CatchDataSummary
                    checked={!!formData.inboundCatchData}
                    values={[
                      { label: "Serial No", value: formData.inboundSerialNo },
                      { label: "IMEI", value: formData.inboundImei },
                      { label: "Other 3", value: formData.inboundOther3 },
                      { label: "Other 4", value: formData.inboundOther4 },
                      { label: "Other 5", value: formData.inboundOther5 },
                    ]}
                  />
                </dd>
                <dt className="col-sm-4">Outbound catch data</dt>
                <dd className="col-sm-8">
                  <CatchDataSummary
                    checked={!!formData.outboundCatchData}
                    values={[
                      { label: "Serial No", value: formData.outboundSerialNo },
                      { label: "IMEI", value: formData.outboundImei },
                      { label: "Other 3", value: formData.outboundOther3 },
                      { label: "Other 4", value: formData.outboundOther4 },
                      { label: "Other 5", value: formData.outboundOther5 },
                    ]}
                  />
                </dd>
                <dt className="col-sm-4">Lot / serial / hazmat</dt>
                <dd className="col-sm-8">
                  {formData.lottracked ? "Lot " : ""}
                  {formData.serialtracked ? "Serial " : ""}
                  {formData.hazmat ? "Hazmat" : ""}
                  {!formData.lottracked && !formData.serialtracked && !formData.hazmat ? "—" : ""}
                </dd>
                <dt className="col-sm-4">Images staged</dt>
                <dd className="col-sm-8">{imageSlots.filter((s) => s.file).length}</dd>
              </dl>
              <p className="small text-muted mt-3 mb-0">
                Use the tabs above to edit sections, then save. Required fields must be complete before submit.
              </p>
            </SectionCard>

            <SectionCard title="Audit trail" iconClass="bi-clock-history">
              <dl className="row small mb-0 text-muted">
                <dt className="col-sm-4">Created by</dt>
                <dd className="col-sm-8">{formData.addwho || "—"}</dd>
                <dt className="col-sm-4">Created on</dt>
                <dd className="col-sm-8">{formData.createdOn || "—"}</dd>
                <dt className="col-sm-4">Updated by</dt>
                <dd className="col-sm-8">{formData.updatedBy || "—"}</dd>
                <dt className="col-sm-4">Updated on</dt>
                <dd className="col-sm-8">{formData.updatedOn || "—"}</dd>
                <dt className="col-sm-4">Approved by</dt>
                <dd className="col-sm-8">{formData.approvedBy || "—"}</dd>
                <dt className="col-sm-4">Approved on</dt>
                <dd className="col-sm-8">{formData.approvedOn || "—"}</dd>
                <dt className="col-sm-4">Version</dt>
                <dd className="col-sm-8">{formData.versionNumber || "—"}</dd>
              </dl>
              <p className="small text-muted mb-0 mt-2">
                Populated from the host system when connected; fields are read-only here.
              </p>
            </SectionCard>

            <SectionCard title="Final notes" iconClass="bi-chat-left-text">
              <FieldLabel htmlFor="remarks" label="Remarks" tooltip="Internal notes included on submit / draft payload." />
              <textarea
                id="remarks"
                name="remarks"
                rows={3}
                className="form-control form-control-sm"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Optional remarks for approvers or operations…"
              />
            </SectionCard>
          </div>
        )}
      </div>
      </fieldset>
      </div>

      <div className="create-sku-form-sticky">
        <span className="small text-muted align-self-center flex-shrink-0">
          {readOnly ? (
            <>
              <i className="bi bi-eye me-1" aria-hidden />
              View mode — all fields are read-only.
            </>
          ) : (
            <>
              <i className="bi bi-shield-check me-1" aria-hidden />
              Required fields marked with <span className="text-danger">*</span>
            </>
          )}
        </span>
        <div className="create-sku-form-sticky-actions">
          <div className="btn-group btn-group-sm" role="group" aria-label="Step navigation">
            <button type="button" className="btn btn-outline-secondary" onClick={goPrev} disabled={loading || tabIdx(activeTab) <= 0}>
              <i className="bi bi-chevron-left" aria-hidden /> Previous
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={goNext}
              disabled={loading || tabIdx(activeTab) >= TAB_ORDER.length - 1}
            >
              Next <i className="bi bi-chevron-right" aria-hidden />
            </button>
          </div>
          <button type="button" className="btn btn-outline-secondary px-4" onClick={onClose} disabled={loading}>
            {readOnly || isEdit ? "Back" : "Cancel"}
          </button>
          {!readOnly && (
            <>
          {!isEdit && (
          <button
            type="button"
            className="btn btn-outline-warning px-3"
            onClick={async () => {
              if (!validate({ focus: true })) {
                return;
              }
              try {
                setLoading(true);
                await sendSkuToWms({ ...buildPayload(), workflowStatus: "Draft" });
                toast.success("Draft saved to WMS");
                if (onClose) onClose();
              } catch (err) {
                toast.error(err?.message || "Draft save failed");
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            Save as Draft
          </button>
          )}
          <button type="button" className="btn btn-primary px-4 shadow-sm" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                Saving…
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle me-2" aria-hidden />
                {isEdit ? "Update SKU" : "Submit"}
              </>
            )}
          </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
